import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useScreenshot } from "use-react-screenshot";
import { useWebSocket } from "../../WebSocketContext";
import "./styles.css";
import { motion } from "framer-motion";
import useSentryLogger from "../../hooks/useSentryLogger";
import logger from './../../utils/logger.js';


// Components
import VideoDisplay from "./components/VideoDisplay";
import TranscriptPanel from "./components/TranscriptPanel";
import ControlPanel from "./components/ControlPanel";
import CodeEditorModal from "./components/CodeEditorModal";
import ConfirmationDialog from "./components/ConfirmationModal/ConfirmationDialog";
import HelpButton from "./components/HelpSupport/HelpButton";
import ContactInfoPopup from "./components/HelpSupport/ContactInfoPopup";
import ProctoringViolationHandler from "../../components/ProctoringViolationHandler";
import ReactLiveCodeEditor from "../../ReactLiveCodeEditor";
import ExcalidrawModal from "./components/ExcalidrawModal";

// Status Overlays
import OfflineOverlay from "./components/StatusOverlays/OfflineOverlay";
import ConnectionStatusOverlay from "./components/StatusOverlays/ConnectionStatusOverlay";
import FullscreenReminder from "./components/StatusOverlays/FullscreenReminder";
import ScreenshotWarning from "./components/StatusOverlays/ScreenshotWarning";
import LoadingOverlay from "./components/StatusOverlays/LoadingOverlay";
import ProctoringModal from "./components/ProctoringModal";

// Custom hooks
// isOnline is now provided by useWebSocket
import useFullscreenStatus from "./hooks/useFullscreenStatus";
import useScreenshotDetection from "./hooks/useScreenshotDetection";
import useMediaStreams from "./hooks/useMediaStreams";
import useScreenshotCapture from "./hooks/useScreenshotCapture";
import { useWindowSize } from "../../useWindowSize";
import InterviewTimer from "./components/InterviewTimer";

// Assets
import backgroundImage from "../../assets/Background.png";
import frame52 from "../../assets/Frame 5(2).png";

// Custom hooks
import useDailyCall from "./hooks/useDailyCall";
import MCQLayout from "../../MCQComponent/MCQLayout";
import toast from "react-hot-toast";

// Code snippets for different languages
const CODE_SNIPPETS = {
  javascript: `// Write your JavaScript code here
function example() {
  logger.debug("Hello, world!");
  return true;
}`,
  python: `# Write your Python code here
def example():
    print("Hello, world!")
    return True`,
  java: `// Write your Java code here
public class Example {
    public static void main(String[] args) {
        System.out.println("Hello, world!");
    }
}`,
  cpp: `// Write your C++ code here
#include <iostream>

int main() {
    std::cout << "Hello, world!" << std::endl;
    return 0;
}`,
  csharp: `// Write your C# code here
using System;

class Program {
    static void Main() {
        Console.WriteLine("Hello, world!");
    }
}`,
};

const Interview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < 700;
  const isTab = width < 1340;

  // Get Sentry logger
  const {
    logInterviewEvent,
    logInterviewError,
    logWebSocketEvent,
    logWebSocketError,
    logPermissionEvent,
    logMessage
  } = useSentryLogger();

  // WebSocket context
  const {
    ws,
    evaSpeaking,
    transcriptData,
    question,
    showEditor,
    typing,
    editorContent,
    socketData,
    wsMessageEvent,
    setTyping,
    setEditorContent,
    setShowEditor,
    interviewCategory,
    isCodeEditorEnabled,
    sendPermissionChangeEvent,
    sendScreenshotEvent,
    sendFullscreenExitEvent,
    reactCodingQuestion,
    codeLanguage,
    isProctoringEnabled,
    cameraPlaced,
    surroundingRecorded,
    setProctoringEnabled,
    isSecondryCameraJoined,
    setSecondryCameraJoined,
    id,
    questionTimer,
    sendSkipRequest,
    callEnded,
    isMCQComponentVisible,
    showExcalidraw,
    setShowExcalidraw,
    currentQuestionIndex,
    totalQuestions,
    sendCameraPermissionSuccess,
    sendMicrophonePermissionSuccess,
    sendBotInteractionSuccess,
    isProctoringResetup
  } = useWebSocket();

  // logger.debug('InterviewTimer props:', { questionTimer, showEditor, sendSkipRequest, question, id });

  // Get interview data from location state or localStorage
  const room_url = location.state?.room_url || localStorage.getItem("room_url");
  const job_interview_id =
    location.state?.job_interview_id ||
    localStorage.getItem("job_interview_id");
  const userName = location.state?.userName || localStorage.getItem("userName");
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

  // Local state
  const [modalType, setModalType] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoaderActive, setLoaderActive] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [isCodeSubmitted, setIsCodeSubmitted] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerDisplay, setTimerDisplay] = useState("");
  const [currentCallback, setCurrentCallback] = useState(() => () => { });
  const [isEditorMinimized, setIsEditorMinimized] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const screenshotRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Organization metadata
  const [orgMetadata, setOrgMetadata] = useState({
    logo: frame52,
    colorCode: "#448df4",
  });

  // Custom hooks
  const { isOnline, reconnectionStatus, reconnectAttempts, maxReconnectAttempts, manualRetryConnection } = useWebSocket();
  const { isFullScreen, timeElapsed, enterFullScreen } = useFullscreenStatus(
    sendFullscreenExitEvent
  );
  const { screenshotDetected, setScreenshotDetected } =
    useScreenshotDetection(sendScreenshotEvent);
  const { isSpeaking } = useMediaStreams(videoRef, isMobile);

  // Screenshot capture functionality
  const [image, takeScreenshot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  useScreenshotCapture(screenshotRef, job_interview_id, BASE_URL);

  // Save interview data to localStorage
  useEffect(() => {
    if (location.state?.job_interview_id) {
      localStorage.setItem("job_interview_id", location.state.job_interview_id);
    }
    if (location.state?.userName) {
      localStorage.setItem("userName", location.state.userName);
    }
    if (location.state?.room_url) {
      localStorage.setItem("room_url", location.state.room_url);
    }
  }, [location.state]);

  // Special handling to persist interview category after refresh
  useEffect(() => {
    // If we have a job_interview_id but no interview category in localStorage, 
    // then we likely refreshed the page and need to set it
    if (job_interview_id) {
      const currentCategory = localStorage.getItem("interviewCategory");
      logger.debug("Current stored interview category:", currentCategory);
      
      if (!currentCategory) {
        logger.debug("No interview category found in localStorage, setting default TECH category");
        // Set a default of TECH which will ensure the buttons are visible
        localStorage.setItem("interviewCategory", "TECH");
        
        // If we have direct access to setInterviewCategory from the WebSocket context, use it
        if (interviewCategory !== "TECH") {
          logger.debug("Updating interview category in context to TECH");
        }
      }
    }
  }, [job_interview_id, interviewCategory]);

  // Scroll to bottom when new transcript data is added
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcriptData]);

  // Navigate to feedback page when interview ends
  useEffect(() => {
    async function navigateToFeedback() {
      // Check if this is a proctoring violation - if so, don't navigate to feedback
      if (socketData?.type === "multiple_side_camera_left" || wsMessageEvent?.type === "multiple_side_camera_left") {

        logger.debug("Proctoring violation detected, skipping feedback navigation");

        return;
      }
      
      if (socketData?.isNavigate && socketData?.type === "ended") {
        // Clean up any Daily.co instances before navigating
        try {
          logger.debug("Cleaning up before navigating");

          // Clean up global Daily.co instance if exists
          if (window?._dailyCallObject) {
            logger.debug("Found global Daily call object, destroying it");
            window._dailyCallObject.destroy();
            window._dailyCallObject = null;
          }

          // Reset the global flag that tracks Daily instances
          if (window?._dailyInstanceActive) {
            logger.debug("Resetting Daily instance active flag");
            window._dailyInstanceActive = false;
          }

          if (window.dispatchEvent) {
            window.dispatchEvent(new Event("end_interview_call"));
            logger.debug("end_interview_call event dispatched");
          }

          // Turn off camera and microphone
          logger.debug("Turning off camera and microphone");
          if (navigator.mediaDevices) {
            const streams = await navigator.mediaDevices
              .getUserMedia({ audio: true, video: true })
              .catch((err) => logger.debug("Failed to get media streams:", err));

            if (streams) {
              streams.getTracks().forEach((track) => {
                track.stop();
                logger.debug(`Stopped ${track.kind} track`);
              });
            }
          }

          // Find and remove ALL iframe elements from the DOM regardless of title
          logger.time("iframe_cleanup_navigation");
          const allIframes = document.querySelectorAll("iframe");
          logger.debug(`Found ${allIframes.length} iframes to remove`);
          allIframes.forEach((iframe) => {
            logger.debug("Removing iframe from DOM:", iframe);
            iframe.parentNode?.removeChild(iframe);
          });
          logger.timeEnd("iframe_cleanup_navigation");

          // Navigate to feedback page
          logger.debug("Navigating to feedback page");
          navigate(`/candidate-feedback/${job_interview_id}`);
        } catch (error) {
          logger.error("Error during call cleanup:", error);
          // Still navigate even if cleanup fails
          navigate(`/candidate-feedback/${job_interview_id}`);
        }
        // navigate('/candidate-feedback', { state: { job_interview_id: socketData?.interview_id } });
      }
    }
    navigateToFeedback();
  }, [socketData, navigate, wsMessageEvent]);

  // Fetch organization metadata
  useEffect(() => {
    if (!job_interview_id) {
      logger.error("Interview ID is not available");
      return;
    }

    const fetchOrgMetadata = async () => {
      const abortController = new AbortController();

      try {
        const response = await fetch(
          `https://${BASE_URL}/api/organisation/meta-data/${job_interview_id}`,
          {
            method: "GET",
           // credentials: "include",
            signal: abortController.signal,
          }
        );

        if (response.status === 200) {
          const data = await response.json();
          setOrgMetadata({
            logo: data.logo_url || frame52,
            colorCode: data.color?.primary || "#448df4",
          });
        } else {
          logger.error(
            "Failed to fetch organization metadata:",
            response.status
          );
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          logger.error("Error fetching organization metadata:", error);
        }
      }
    };

    const abortController = new AbortController();
    fetchOrgMetadata();

    return () => {
      abortController.abort();
    };
  }, [job_interview_id, BASE_URL]);

  // Add Sentry logging for component mount
  useEffect(() => {
    logInterviewEvent('component mounted', {
      job_interview_id,
      userName,
      room_url
    });
  }, []);

  // Add Sentry logging for WebSocket connection status
  useEffect(() => {
    if (ws) {
      // Test Sentry statement
      logMessage('Test Sentry Integration', 'info', {
        tags: {
          test: 'true',
          component: 'Interview'
        }
      });

      logWebSocketEvent(`connection status: ${ws.readyState === WebSocket.OPEN ? 'connected' : 'disconnected'}`, {
        readyState: ws.readyState,
        job_interview_id
      });
    }
  }, [ws?.readyState]);

  // Handle code submission
  const handleSubmit = (e, callback = () => { }) => {
    logger.debug("xxxxxxxx");
    e.preventDefault();
    if (e.isAutoSubmit) {
      // For autosubmit, pass the actual code value from the editor
      submitQuestion(callback, e.value);
      return;
    }
    setCurrentCallback(() => callback);
    setModalType("submit_code");
    setIsModalOpen(true);
  };

  const submitQuestion = async (callback = () => { }, codeValue = null) => {
    logInterviewEvent('code submission attempted', {
      job_interview_id,
      question_id: id,
      language: codeLanguage
    });

    // Use the provided codeValue (from autosubmit) or fall back to typing state (for manual submit)
    const answerValue = codeValue !== null ? codeValue : typing;

    const data = JSON.stringify({
      interview_id: job_interview_id,
      question: question,
      question_id: id,
      answer: answerValue,
      language: codeLanguage || "javascript",
    });

    setLoaderActive(true);
    setLoaderMessage("Please wait, answer is submitting...");
    try {
      const res = await fetch(`https://${BASE_URL}/api/coding-question`, {
        method: "post",
       // credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      });
      setLoaderMessage("Submitted");
      setIsCodeSubmitted(true);
      
      logInterviewEvent('code submitted successfully', {
        job_interview_id,
        question_id: id,
        status: res.status
      });
      
      // Save the question as submitted in localStorage
      if (id) {
        const submittedQuestionsKey = `submittedQuestions_${job_interview_id}`;
        const currentSubmitted = localStorage.getItem(submittedQuestionsKey);
        const submittedQuestions = currentSubmitted ? JSON.parse(currentSubmitted) : {};
        submittedQuestions[id] = true;
        localStorage.setItem(submittedQuestionsKey, JSON.stringify(submittedQuestions));

        // Also mark this specific timer as paused
        const timerPausedKey = `codeEditor_timer_paused_${job_interview_id}_${id}`;
        localStorage.setItem(timerPausedKey, 'true');
      }
      
      callback({
        success: true,
      });
      setTimeout(() => {
        setLoaderActive(false);
        setLoaderMessage("");
      }, 2000);
    } catch (error) {
      logInterviewError(error, {
        tags: { 
          action: 'code_submission',
          job_interview_id,
          question_id: id
        }
      });
      setLoaderActive(false);
      setLoaderMessage("");
      logger.debug("Something went wrong", error);
    }
  };

  const autoSaveCode = async () => {
    return;
    if (reactCodingQuestion) return;
    try {
      logger.debug("Auto-save triggered");

      // Prepare the payload with interview_id from local storage and question_id from WebSocket context
      const payload = {
        interview_id: job_interview_id,
        question: question,
        question_id: id, // Make sure this is using the id from WebSocket context
        answer: typing,
        language: codeLanguage || "javascript",
      };

      logger.debug("Sending auto-save payload:", payload);

      // Send the request to the API
      const response = await fetch(`https://${BASE_URL}/api/auto-save-code`, {
        method: "POST",
       // credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error("Failed to auto-save code:", response.status, errorData);
      } else {
        logger.debug("Code auto-saved successfully");
        setLastAutoSaveTime(Date.now());
      }
    } catch (error) {
      logger.error("Error auto-saving code:", error);
    }
  };

  const leaveCall = () => {
    //end_interview_call
    callEnded();
    window.dispatchEvent(new CustomEvent("end_interview_call"));
  };

  // Dialog confirmation handlers
  const handleConfirm = async () => {
    if (modalType === "end_call") {
      // Clean up any Daily.co instances before navigating
      try {
        logger.debug("Cleaning up before navigating");

        // Clean up global Daily.co instance if exists
        if (window?._dailyCallObject) {
          logger.debug("Found global Daily call object, destroying it");
          window._dailyCallObject.destroy();
          window._dailyCallObject = null;
        }

        // Reset the global flag that tracks Daily instances
        if (window?._dailyInstanceActive) {
          logger.debug("Resetting Daily instance active flag");
          window._dailyInstanceActive = false;
        }

        if (window.dispatchEvent) {
          window.dispatchEvent(new Event("end_interview_call"));
          logger.debug("end_interview_call event dispatched");
        }

        // Turn off camera and microphone
        logger.debug("Turning off camera and microphone");
        if (navigator.mediaDevices) {
          const streams = await navigator.mediaDevices
            .getUserMedia({ audio: true, video: true })
            .catch((err) => logger.debug("Failed to get media streams:", err));

          if (streams) {
            streams.getTracks().forEach((track) => {
              track.stop();
              logger.debug(`Stopped ${track.kind} track`);
            });
          }
        }

        // Find and remove ALL iframe elements from the DOM regardless of title
        const allIframes = document.querySelectorAll("iframe");
        logger.debug(`Found ${allIframes.length} iframes to remove`);
        allIframes.forEach((iframe) => {
          logger.debug("Removing iframe from DOM:", iframe);
          iframe.parentNode?.removeChild(iframe);
        });

        // Navigate to feedback page
        logger.debug("Navigating to feedback page");
        navigate(`/candidate-feedback/${job_interview_id}`);
      } catch (error) {
        logger.error("Error during call cleanup:", error);
        // Still navigate even if cleanup fails
        navigate(`/candidate-feedback/${job_interview_id}`);
      }
    } else if (modalType === "submit_code") {
      submitQuestion(currentCallback);
    } else {
      submitQuestion(currentCallback);
      setShowEditor(false);
    }
    setIsModalOpen(false);
    setModalType("");
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setModalType("");
  };

  // Reschedule interview
  const rescheduleInterview = async () => {
    setLoaderActive(true);
    setLoaderMessage("Rescheduling Interview....");
    try {
      const res = await fetch(
        `https://${BASE_URL}/api/jobinterviews/reschedule-request?jobinterview_id=${job_interview_id}`,
        {
          method: "post",
         // credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: {},
        }
      );
      setLoaderMessage("Requested!");
      setIsModalOpen(false);
      setModalType("");
    } catch (error) {
      logger.error("Error requesting reschedule:", error);
      setLoaderMessage("Request Failed");
      setIsModalOpen(false);
      setModalType("");
    } finally {
      setTimeout(() => {
        setLoaderActive(false);
        setLoaderMessage("");
      }, 2000);
    }
  };

  // Control panel handlers
  const handleEndCall = () => {
    logInterviewEvent('end call requested', {
      job_interview_id,
      userName
    });
    logger.debug("handleEndCall function called in index.jsx");
    setModalType("end_call");
    setIsModalOpen(true);
  };

  const handleEditorClose = () => {
    // Instead of completely closing the editor, just minimize it
    
    setIsEditorMinimized(true);
    
  };

  const handleEditorOpen = () => {
    logger.debug("handleEditorOpen function called in index.jsx");
    const snippet =
      CODE_SNIPPETS[interviewCategory?.toLowerCase()] ||
      CODE_SNIPPETS.javascript;
    const newContent = ` ${typing}\n\n${snippet}`;
    setEditorContent(newContent);
    setShowEditor(true);
    setIsEditorMinimized(false);
  };
  
  const handleEditorToggle = () => {
    logInterviewEvent('editor state toggled', {
      job_interview_id,
      showEditor,
      isEditorMinimized
    });
    logger.debug("handleEditorToggle function called in index.jsx");
    if (showEditor) {
      setIsEditorMinimized(!isEditorMinimized);
    } else {
      const snippet =
        CODE_SNIPPETS[interviewCategory?.toLowerCase()] ||
        CODE_SNIPPETS.javascript;
      const newContent = ` ${typing}\n\n${snippet}`;
      setEditorContent(newContent);
      setShowEditor(true);
      setIsEditorMinimized(false);
    }
  };

  // Function to handle timer updates from CodeEditor
  const handleTimerChange = (timerData) => {
    if (timerData && typeof timerData === "object") {
      // Handle the new format with seconds and displayValue
      setTimerSeconds(timerData.seconds);
      setTimerDisplay(timerData.displayValue);

      // If timer has reached zero, ensure we show 00:00
      if (timerData.seconds <= 0) {
        setTimerSeconds(0);
        setTimerDisplay("00:00");
      }
    } else if (typeof timerData === "number") {
      // Handle legacy format (just seconds)
      setTimerSeconds(timerData);
      // Format seconds to mm:ss
      const minutes = Math.floor(timerData / 60);
      const seconds = timerData % 60;
      setTimerDisplay(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      );

      // If timer has reached zero, ensure we show 00:00
      if (timerData <= 0) {
        setTimerSeconds(0);
        setTimerDisplay("00:00");
      }
    }
  };

  const toggleContactInfo = () => {
    setShowContactInfo((prev) => !prev);
  };

  // Prevent page exit and keep tab active in background
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const message =
        "Are you sure you want to leave? Changes you made may not be saved.";
      event.returnValue = message;
      return message;
    };

    // Keep the page active using a silent audio technique
    let silentAudio = null;
    let backgroundKeepAliveTimer = null;

    // Function to keep the page alive in the background
    const keepAliveInBackground = () => {
      // Create silent audio element if it doesn't exist
      if (!silentAudio) {
        silentAudio = new Audio();
        silentAudio.src =
          "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
        silentAudio.loop = true;
        silentAudio.volume = 0.001; // Nearly silent
      }

      // Setup periodic timer to keep the tab active
      backgroundKeepAliveTimer = setInterval(() => {
        if (document.hidden) {
          // Play silent audio to keep tab active
          const playPromise = silentAudio.play();

          if (playPromise !== undefined) {
            playPromise.catch((e) => {
              // Auto-play was prevented, try alternative keepalive
              logger.debug("Silent audio blocked, using alternative keepalive");

              // Force minimal CPU work to prevent deep throttling
              window.dispatchEvent(new Event("resize"));
            });
          }

          // Force minimal CPU activity to keep tab alive
          document.title = document.title;
        } else if (silentAudio) {
          // Pause when visible to conserve resources
          silentAudio.pause();
        }
      }, 5000); // Check every 5 seconds
    };

    // Start the keepalive mechanism
    keepAliveInBackground();

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Clean up background keepalive resources
      if (backgroundKeepAliveTimer) {
        clearInterval(backgroundKeepAliveTimer);
      }

      if (silentAudio) {
        silentAudio.pause();
        silentAudio.src = "";
        silentAudio = null;
      }
    };
  }, []);

  // Detect tab switching
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Show a notification instead of an alert to avoid disrupting the video call
        // Using a more subtle notification that doesn't block execution
        const tabSwitchWarning = document.createElement("div");
        tabSwitchWarning.style.position = "fixed";
        tabSwitchWarning.style.top = "10px";
        tabSwitchWarning.style.left = "50%";
        tabSwitchWarning.style.transform = "translateX(-50%)";
        tabSwitchWarning.style.backgroundColor = "rgba(255, 0, 0, 0.8)";
        tabSwitchWarning.style.color = "white";
        tabSwitchWarning.style.padding = "10px 20px";
        tabSwitchWarning.style.borderRadius = "5px";
        tabSwitchWarning.style.zIndex = "10000";
        tabSwitchWarning.style.fontSize = "14px";
        tabSwitchWarning.textContent =
          "Tab switching detected. Please return to the interview tab.";
        document.body.appendChild(tabSwitchWarning);

        // Remove the warning when tab is visible again
        setTimeout(() => {
          if (tabSwitchWarning.parentNode) {
            tabSwitchWarning.parentNode.removeChild(tabSwitchWarning);
          }
        }, 5000); // Remove after 5 seconds
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleSubmitMCQ = async (payload) => {
    logInterviewEvent('MCQ submission attempted', {
      job_interview_id,
      payload
    });

    const abortController = new AbortController();
    const raw = { ...payload, interview_id: job_interview_id }
    try {
      const response = await fetch(
        `https://${BASE_URL}/api/mcq-question`,
        {
          method: "POST",
         // credentials: "include",
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(raw),
          signal: abortController.signal,
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        logInterviewEvent('MCQ submitted successfully', {
          job_interview_id,
          status: response.status
        });
        toast.success("Submitted");
        return true;
      } else {
        logMessage('Failed to submit MCQ', 'error', {
          tags: {
            job_interview_id,
            status: response.status
          }
        });
        logger.error(
          "Failed to fetch organization metadata:",
          response.status
        );
        return false;
      }
    } catch (error) {
      logInterviewError(error, {
        tags: {
          action: 'mcq_submission',
          job_interview_id
        }
      });
      if (error.name !== "AbortError") {
        logger.error("Error fetching organization metadata:", error);
        return false;
      }
    }
    return false;
  };

  // Handle Excalidraw toggle
  const handleExcalidrawToggle = () => {
    setShowExcalidraw(prev => !prev);
  };

  const handleExcalidrawClose = () => {
    setShowExcalidraw(false);
  };

  // New state for tracking permission status
  const [permissionStatus, setPermissionStatus] = useState({
    camera: false,
    microphone: false,
    botInteraction: false
  });

  // Add WebSocket reference
  const WebSocket = window.WebSocket || global.WebSocket;

  // Function to check if an event has been sent for current interview
  const hasEventBeenSent = (eventType) => {
    const jobInterviewId = localStorage.getItem("job_interview_id");
    if (!jobInterviewId) return false;
    
    const sentEvents = localStorage.getItem(`sent_events_${jobInterviewId}`);
    if (!sentEvents) return false;
    
    const parsedEvents = JSON.parse(sentEvents);
    return parsedEvents[eventType] === true;
  };

  // Function to mark an event as sent for current interview
  const markEventAsSent = (eventType) => {
    const jobInterviewId = localStorage.getItem("job_interview_id");
    if (!jobInterviewId) return;
    
    const sentEvents = localStorage.getItem(`sent_events_${jobInterviewId}`);
    const parsedEvents = sentEvents ? JSON.parse(sentEvents) : {};
    
    parsedEvents[eventType] = true;
    localStorage.setItem(`sent_events_${jobInterviewId}`, JSON.stringify(parsedEvents));
  };

  // Handle permission events when the component mounts or when permission status changes
  useEffect(() => {
    // Function to check permissions and send events
    const checkAndSendPermissionEvents = async () => {
      try {
        // Check camera permission
        if (!permissionStatus.camera && !hasEventBeenSent('camera')) {
          try {
            const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (cameraStream) {
              logger.debug('Camera permission granted, sending success event');
              sendCameraPermissionSuccess();
              setPermissionStatus(prev => ({ ...prev, camera: true }));
              markEventAsSent('camera');
              // Stop tracks after checking
              cameraStream.getTracks().forEach(track => track.stop());
            }
          } catch (err) {
            logger.debug('Camera permission not yet granted:', err);
          }
        }
        
        // Check microphone permission
        if (!permissionStatus.microphone && !hasEventBeenSent('microphone')) {
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (micStream) {
              logger.debug('Microphone permission granted, sending success event');
              sendMicrophonePermissionSuccess();
              setPermissionStatus(prev => ({ ...prev, microphone: true }));
              markEventAsSent('microphone');
              // Stop tracks after checking
              micStream.getTracks().forEach(track => track.stop());
            }
          } catch (err) {
            logger.debug('Microphone permission not yet granted:', err);
          }
        }
        
        // For bot interaction, we rely on the Daily.co connection
        if (!permissionStatus.botInteraction && !hasEventBeenSent('botInteraction') && ws && ws.readyState === WebSocket.OPEN) {
          // Send bot interaction success after a delay to ensure room is created
          setTimeout(() => {
            sendBotInteractionSuccess();
            setPermissionStatus(prev => ({ ...prev, botInteraction: true }));
            markEventAsSent('botInteraction');
            logger.debug('Bot interaction permission sent');
          }, 3000);
        }
      } catch (error) {
        logger.error('Error checking permissions:', error);
      }
    };
    
    // Call the function on mount and when WebSocket is connected
    if (ws && ws.readyState === WebSocket.OPEN) {
      checkAndSendPermissionEvents();
    }
    
    // Set up periodic checks (every 5 seconds) until all permissions are granted
    const permissionCheckInterval = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN && 
          (!permissionStatus.camera || !permissionStatus.microphone || !permissionStatus.botInteraction)) {
        checkAndSendPermissionEvents();
      } else if (permissionStatus.camera && permissionStatus.microphone && permissionStatus.botInteraction) {
        // All permissions granted, clear interval
        clearInterval(permissionCheckInterval);
      }
    }, 5000);
    
    return () => {
      clearInterval(permissionCheckInterval);
    };
  }, [
    ws, 
    permissionStatus, 
    sendCameraPermissionSuccess, 
    sendMicrophonePermissionSuccess, 
    sendBotInteractionSuccess,
    WebSocket
  ]);

  // Add Sentry logging for permission status changes
  useEffect(() => {
    if (permissionStatus.camera || permissionStatus.microphone || permissionStatus.botInteraction) {
      logPermissionEvent('status updated', {
        job_interview_id,
        camera: permissionStatus.camera,
        microphone: permissionStatus.microphone,
        botInteraction: permissionStatus.botInteraction
      });
    }
  }, [permissionStatus]);

  return (
    <div className="interview-container">
      <div 
        ref={screenshotRef} 
        style={{ 
          height: "100vh", 
          width: "100vw",
          position: "relative",
          overflow: "hidden",
          zIndex: 1 // Add z-index to ensure proper stacking
        }}
      >
        {/* Status Overlays */}
        {isProctoringEnabled && (
          <ProctoringModal
            socketData={socketData}
            isProctoringEnabled={isProctoringEnabled}
            isProctoringResetup={isProctoringResetup}
            isSecondryCameraJoined={isSecondryCameraJoined}
            surroundingRecorded={surroundingRecorded}
            cameraPlaced={cameraPlaced}
            setProctoringEnabled={setProctoringEnabled}
            setSecondryCameraJoined={setSecondryCameraJoined}
          />
        )}

        {!isOnline && <OfflineOverlay job_interview_id={job_interview_id} />}

        {/* Connection status overlay for WebSocket reconnection */}
        {isOnline &&
          (reconnectionStatus === "loading" ||
            reconnectionStatus === "connecting" ||
            reconnectionStatus === "max_attempts_reached") && (
            <ConnectionStatusOverlay
              reconnectionStatus={reconnectionStatus}
              reconnectAttempts={reconnectAttempts}
              maxReconnectAttempts={maxReconnectAttempts}
              onRetry={manualRetryConnection}
            />
          )}

        {!isFullScreen && !isMobile && (
          <FullscreenReminder
            enterFullScreen={enterFullScreen}
            timeElapsed={timeElapsed}
          />
        )}

        {screenshotDetected && (
          <ScreenshotWarning setScreenshotDetected={setScreenshotDetected} />
        )}

        {isLoaderActive && <LoadingOverlay loaderMessage={loaderMessage} />}

        <div
          style={{
            background: "#fff",
            justifyContent: "center",
            zIndex: 1,
            position: "absolute",
            height: "100vh",
            width: "100vw",
            display: "flex",
            alignItems: "center",
            overflowY: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              height: isMobile ? "60px" : "80px",
              background: "#fff",
              display: isMobile ? "none" : "flex", // Hide header on mobile
              width: "100vw",
              position: isMobile ? "absolute" : "fixed",
              top: isMobile ? 10 : 0,
              padding: "10px 20px",
              borderBottom: "1px solid #DADADD",
              zIndex: isMobile ? 1 : 1001,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <div
                className="absolute top-4"
                style={{
                  left: isMobile ? "5px" : "50px",
                  width: "auto",
                  height: "28px",
                }}
              >
                <img
                  src={orgMetadata.logo}
                  alt="Organization Logo"
                  style={{
                    maxWidth: isMobile ? "100px" : "140px",
                    maxHeight: isMobile ? "30px" : "40px",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>

            {questionTimer && !reactCodingQuestion && !showEditor && (
              <InterviewTimer
                questionTimer={questionTimer}
                showEditor={showEditor}
                sendSkipRequest={sendSkipRequest}
                question={question}
                id={id}
                submitQuestion={submitQuestion}
                setModalType={setModalType}
                setModalOpen={setModalOpen}
                autoSaveCode={autoSaveCode}
              />
            )}
          </div>

          {/* Main Content */}
          <div
            className="main-content"
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "0px" : "20px",
              height: isMobile ? "calc(100vh - 40px)" : "87vh",
              width: "100%",
              marginTop: isMobile ? "0px" : "20px",
              padding: isMobile ? "0px" : "15px",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            {/* Left side: Video or MCQ */}
            <div
              className="left-content"
              style={{
                position: "relative",

                marginTop:isMobile?"0px": "60px",
                width: isMobile
                  ? "100%"
                  : isMCQComponentVisible
                  ? "67%"
                  : "67vw",
                height: isMobile ? "30vh" : "100%",
                minHeight: isMobile ? "30vh" : "auto",
                flexShrink: 0,
                marginBottom: isMobile ? "0px" : "0",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              {/*  Desktop Question Label */}
              {!isMobile &&
                currentQuestionIndex != null &&
                !isNaN(currentQuestionIndex) &&
                totalQuestions != null &&
                !isNaN(totalQuestions) && (
                  <>
                    {/* Progress Bar */}
                    {/* <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "6px",
                        width: "100%",
                        backgroundColor: "#e5e7eb",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                        overflow: "hidden",
                        zIndex: 299,
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${
                            ((currentQuestionIndex + 1) / totalQuestions) * 100
                          }%`,
                          backgroundColor: "#3b82f6",
                          transition: "width 0.3s ease-in-out",
                        }}
                      ></div>
                    </div> */}

                    {/* Question Label */}
                    {/* <div
                      style={{
                        position: "absolute",
                        top: 15,
                        left: 12,
                        zIndex: 300,
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#1f2937",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        pointerEvents: "none",
                      }}
                    >
                      Question {currentQuestionIndex } of {totalQuestions} 
                    </div> */}
                  </>
                )}

              <VideoDisplay
                videoRef={videoRef}
                userName={userName}
                isSpeaking={isSpeaking}
                evaSpeaking={evaSpeaking}
                roomUrl={room_url}
                showEditor={showEditor}
                isMobile={isMobile}
                isProctoringJoined={isSecondryCameraJoined}
                question={question}
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={totalQuestions}
                isMCQComponentVisible={isMCQComponentVisible}
              />

              {/* MCQ Component */}
              {isMCQComponentVisible && (
                <div
                  className="mcq-container"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 200,
                  }}
                >
                  <MCQLayout
                    handleSubmitMCQ={handleSubmitMCQ}
                    interviewId={job_interview_id}
                    totalQuestions={totalQuestions} 
                    currentQuestionIndex={currentQuestionIndex}

                  />
                </div>
              )}
            </div>

            {/* Mobile Question Box */}
            {isMobile && (
              <div
                className="mobile-question-box"
                style={{
                  width: "100%",
                  height: "25vh",
                  minHeight: "25vh",
                  backgroundColor: "#f0f9ff",
                  paddingRight: "12px",
                  paddingTop:"5px",
                  paddingBottom:"5px",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                    paddingBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#3b82f6",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "8px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: "#ffffff",
                    }}
                  >
                    Q
                  </div>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "#0f172a",
                    }}
                  >
                    {question ? "Current Question" : ""}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "1.3",
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "4px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {question ? question : "Your Question will be displayed here"}
                </div>
              </div>
            )}

            {/* Right side: Transcript Panel */}
            <div
              className="right-content"
              style={{
                width: isMobile ? "100%" : "30%",
                height: isMobile ? "35vh" : "100%",
                minHeight: isMobile ? "35vh" : "auto",
                flexShrink: 0,
                marginBottom: isMobile ? "40px" : "0",
                marginTop: isMobile ? "0" : "65px",
                boxSizing: "border-box",
              }}
            >
              <TranscriptPanel
                transcriptData={transcriptData}
                transcriptEndRef={transcriptEndRef}
                isMobile={isMobile}
              />
            </div>

            {/* Excalidraw overlay */}
            <div className="w-full h-full absolute top- left-0 right-0 bottom-0 p-5">
              {showExcalidraw && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  zIndex: 9999,
                }}
              >
                <ExcalidrawModal
                  onClose={handleExcalidrawClose}
                  isMobile={isMobile}
                  id={id}
                  interviewId={job_interview_id}
                  question={question}
                  sendSkipRequest={sendSkipRequest}
                />
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <ControlPanel
          isMobile={true}
          handleEndCall={handleEndCall}
          isCodeEditorEnabled={isCodeEditorEnabled}
          handleEditorToggle={handleEditorToggle}
          showEditor={showEditor}
          reactCodingQuestion={reactCodingQuestion}
          isEditorMinimized={isEditorMinimized}
          handleExcalidrawOpen={handleExcalidrawToggle}
          showExcalidraw={showExcalidraw}
          // editorButtonStyle={{
            // display: 'flex',
          //   alignItems: 'center',
          //   justifyContent: 'center',
          //   padding: '12px',
          //   fontSize: '16px'
          // }}
        />

        {/* Timer Display - Only show when editor is open */}
        {questionTimer &&
          !reactCodingQuestion &&
          showEditor &&
          !isEditorMinimized && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "6%", // Always use top 6% now that we only show this when editor is open
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 16px",
                borderRadius: "20px",
                backgroundColor: timerSeconds <= 30 ? "#ffeeee" : "#f8f9fa",
                boxShadow:
                  timerSeconds <= 30
                    ? "0 2px 8px rgba(255,0,0,0.2)"
                    : "0 2px 4px rgba(0,0,0,0.1)",
                fontWeight: "bold",
                fontSize: "14px",
                letterSpacing: "0.5px",
                color: timerSeconds <= 60 ? "#FF3333" : "#343a40",
                zIndex: 100200,
              }}
            >
              <span
                role="img"
                aria-label="Timer"
                style={{ marginRight: "8px", fontSize: "16px" }}
              >
                ⏱️
              </span>
              {timerDisplay || questionTimer}
            </div>
          )}

        {/* Code Editor Modal */}
        {showEditor && !reactCodingQuestion && (
          <CodeEditorModal
            onClose={handleEditorClose}
            onChangeInputValue={(value) => {
              setEditorContent(value);
              setTyping(value);
            }}
            // onLanguageChange={(lang) => setCodeLanguage(lang)}
            onSubmit={handleSubmit}
            initialValue={editorContent}
            question_remarks={typing}
            interviewId={job_interview_id}
            backgroundImage={backgroundImage}
            onTimerChange={handleTimerChange}
            autoSaveCode={autoSaveCode}
            question={question}
            isMinimized={isEditorMinimized}
          />
        )}

        {/* React Live Code Editor - Move inside screenshotRef container */}
        {reactCodingQuestion && !isEditorMinimized && (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
            }}
          >
            <ReactLiveCodeEditor
              interviewId={job_interview_id}
              question={question}
              autoSaveCode={autoSaveCode}
            />
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isModalOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        modalType={modalType}
        onRequest={rescheduleInterview}
        leaveCall={leaveCall}
      />

      {/* Help & Support */}
      {!isMobile && !(reactCodingQuestion && !isEditorMinimized) && (
        <HelpButton isMobile={isMobile} toggleContactInfo={toggleContactInfo} />
      )}

      {showContactInfo && !(reactCodingQuestion && !isEditorMinimized) && <ContactInfoPopup />}
      
      {/* Proctoring Violation Handler - listens for multiple_side_camera_left event */}
      <ProctoringViolationHandler />
    </div>
  );
};

export default Interview;